import { PaymentGateway, PaymentMethod, SPREEDLY_OFFSITE_METHODS } from '../../services/exports/Constants';

/**
 * The backend's gateway value for PayPal-direct (`App\Constants\PaymentGateway::PaypalStandalone`).
 * Not a member of the frontend PaymentGateway enum: that enum drives SDK routing, and PayPal-direct
 * loads no gateway SDK.
 */
export const PAYPAL_STANDALONE_GATEWAY = 'paypal_standalone';

export type AnalyticsPaymentGateway = PaymentGateway | typeof PAYPAL_STANDALONE_GATEWAY;

/** The methods that settle straight to the restaurant, outside any gateway: `PaymentMethod::directSettlement()`. */
const DIRECT_SETTLEMENT_METHODS: readonly string[] = [PaymentMethod.Cash, PaymentMethod.PaypalStandalone];

/**
 * The methods each gateway processes. A copy of shop-backend's `PaymentMethod::stripe()`, `::adyen()` and
 * `::spreedly()` (app/Constants/PaymentOption/PaymentMethod.php), the sets its PaymentGatewayResolver checks:
 * keep the three in step with those, or begin_purchase / complete_purchase and the backend's order_placed
 * report different gateways for the same payment.
 */
export const GATEWAY_PAYMENT_METHODS: Readonly<Record<PaymentGateway, readonly PaymentMethod[]>> = {
  // Defined like the backend's: every method except the direct-settlement ones and the Spreedly-only
  // offsite ones, which native Stripe Connect never requested the capabilities for.
  [PaymentGateway.Stripe]: Object.values(PaymentMethod).filter(
    (method) => !DIRECT_SETTLEMENT_METHODS.includes(method) && !SPREEDLY_OFFSITE_METHODS.includes(method),
  ),
  [PaymentGateway.Adyen]: [
    PaymentMethod.Card,
    PaymentMethod.ApplePay,
    PaymentMethod.GooglePay,
    PaymentMethod.PayByBank,
  ],
  // Card, plus the offsite methods: the Spreedly-only ones and PayPal, like the backend's `::spreedlyOffsite()`.
  [PaymentGateway.Spreedly]: [PaymentMethod.Card, ...SPREEDLY_OFFSITE_METHODS, PaymentMethod.Paypal],
};

/**
 * The gateway that processes a payment made with `method` — the value the backend stores on the
 * transaction and sends as `order_placed.payment_gateway`. Mirrors shop-backend's
 * PaymentGatewayResolver, so begin_purchase, complete_purchase and order_placed split the checkout
 * funnel the same way:
 *
 * - no method / cash → null (nothing processes it)
 * - paypal_standalone (PayPal-direct) → paypal_standalone
 * - paypal → null unless the company lists it in supported_payment_methods, which the backend
 *   does only where a gateway can take it; then spreedly on a Spreedly restaurant (the partner's
 *   own PayPal or Braintree connection), else stripe when Stripe is verified, else null
 * - anything else → the primary (first verified) gateway when it processes the method
 *   (GATEWAY_PAYMENT_METHODS), else null: the secondary gateway only ever takes PayPal
 *
 * `method` must be the method the order is CHARGED with (`paypal_standalone` for PayPal-direct), not
 * the checkout UI's single "PayPal" entry — PaymentFlowProvider.getGatewayForMethod answers "which SDK
 * to load" and returns null for PayPal-direct.
 */
export function resolveAnalyticsPaymentGateway(
  method: null | undefined | PaymentMethod | string,
  gateways: null | undefined | PaymentGateway[],
  supportedMethods: null | undefined | readonly (PaymentMethod | string)[] = null,
): null | AnalyticsPaymentGateway {
  if (!method || method === PaymentMethod.Cash) {
    return null;
  }

  if (method === PaymentMethod.PaypalStandalone) {
    return PAYPAL_STANDALONE_GATEWAY;
  }

  const verified = gateways ?? [];
  const primary = verified[0];

  if (method === PaymentMethod.Paypal) {
    if (!supportedMethods?.includes(PaymentMethod.Paypal)) {
      return null;
    }

    if (primary === PaymentGateway.Spreedly) {
      return PaymentGateway.Spreedly;
    }

    return verified.includes(PaymentGateway.Stripe) ? PaymentGateway.Stripe : null;
  }

  // Optional chaining: the payload may name a gateway this build does not know, which processes nothing here.
  return primary && GATEWAY_PAYMENT_METHODS[primary]?.includes(method as PaymentMethod) ? primary : null;
}
